/**
 * Product Videos for Collection Grid
 * Handles video autoplay, controls, and optimization
 */

class ProductVideoManager {
  constructor() {
    this.videos = [];
    this.intersectionObserver = null;
    this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.isMobile = window.innerWidth <= 768;
    
    this.init();
  }

  init() {
    this.setupIntersectionObserver();
    this.bindEvents();
    this.initializeVideos();
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      // Fallback for browsers without IntersectionObserver
      this.handleVideoVisibility();
      return;
    }

    const options = {
      root: null,
      rootMargin: '50px',
      threshold: 0.3
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        const container = video.closest('.product-block__video-container');
        
        if (entry.isIntersecting) {
          this.playVideo(video);
          container?.classList.add('in-view');
        } else {
          this.pauseVideo(video);
          container?.classList.remove('in-view');
        }
      });
    }, options);
  }

  initializeVideos() {
    const videoElements = document.querySelectorAll('[data-product-video]');
    
    videoElements.forEach(video => {
      this.setupVideo(video);
    });
  }

  setupVideo(video) {
    const container = video.closest('.product-block__video-container');
    if (!container) return;

    // Add video to tracking array
    this.videos.push({
      element: video,
      container: container,
      isPlaying: false,
      isMuted: video.muted
    });

    // Set up video properties
    video.playsInline = true;
    video.preload = 'metadata';
    
    // Add loading state
    container.classList.add('loading');

    // Handle video events
    video.addEventListener('loadeddata', () => {
      container.classList.remove('loading');
      container.classList.add('loaded');
    });

    video.addEventListener('error', () => {
      container.classList.remove('loading');
      container.classList.add('error');
      console.warn('Product video failed to load:', video.src);
    });

    video.addEventListener('play', () => {
      this.updateVideoState(video, true);
    });

    video.addEventListener('pause', () => {
      this.updateVideoState(video, false);
    });

    // Set up controls if enabled
    this.setupVideoControls(video, container);

    // Start observing for intersection
    if (this.intersectionObserver) {
      this.intersectionObserver.observe(video);
    }

    // Handle reduced motion preference
    if (this.isReducedMotion) {
      video.autoplay = false;
    }
  }

  setupVideoControls(video, container) {
    const playPauseBtn = container.querySelector('.video-play-pause');
    const muteBtn = container.querySelector('.video-mute-unmute');

    if (playPauseBtn) {
      playPauseBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.togglePlayPause(video);
      });
    }

    if (muteBtn) {
      muteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleMute(video);
      });
    }
  }

  playVideo(video) {
    if (this.isReducedMotion) return;
    
    const videoData = this.videos.find(v => v.element === video);
    if (!videoData) return;

    // Only autoplay if the setting is enabled and video is muted
    const shouldAutoplay = video.hasAttribute('autoplay') && video.muted;
    
    if (shouldAutoplay) {
      video.play().catch(error => {
        console.warn('Video autoplay failed:', error);
        // Fallback: ensure video is muted and try again
        video.muted = true;
        video.play().catch(() => {
          // If still fails, disable autoplay for this video
          video.removeAttribute('autoplay');
        });
      });
    }
  }

  pauseVideo(video) {
    if (!video.paused) {
      video.pause();
    }
  }

  togglePlayPause(video) {
    const container = video.closest('.product-block__video-container');
    const playPauseBtn = container?.querySelector('.video-play-pause');
    
    if (video.paused) {
      video.play().then(() => {
        playPauseBtn?.classList.add('playing');
      }).catch(error => {
        console.warn('Video play failed:', error);
      });
    } else {
      video.pause();
      playPauseBtn?.classList.remove('playing');
    }
  }

  toggleMute(video) {
    const container = video.closest('.product-block__video-container');
    const muteBtn = container?.querySelector('.video-mute-unmute');
    
    video.muted = !video.muted;
    
    if (video.muted) {
      muteBtn?.classList.add('muted');
    } else {
      muteBtn?.classList.remove('muted');
    }

    // Update video data
    const videoData = this.videos.find(v => v.element === video);
    if (videoData) {
      videoData.isMuted = video.muted;
    }
  }

  updateVideoState(video, isPlaying) {
    const videoData = this.videos.find(v => v.element === video);
    if (videoData) {
      videoData.isPlaying = isPlaying;
    }

    const container = video.closest('.product-block__video-container');
    const playPauseBtn = container?.querySelector('.video-play-pause');
    
    if (isPlaying) {
      playPauseBtn?.classList.add('playing');
    } else {
      playPauseBtn?.classList.remove('playing');
    }
  }

  bindEvents() {
    // Handle window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.isMobile = window.innerWidth <= 768;
        this.handleResponsiveChanges();
      }, 250);
    });

    // Handle visibility change (tab switching)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAllVideos();
      } else {
        this.resumeVisibleVideos();
      }
    });

    // Simple video resume handling (no navigation needed for video-only products)
    document.addEventListener('click', (e) => {
      // Just ensure videos resume if needed
      if (e.target.closest('.product-block')) {
        setTimeout(() => {
          this.resumeActiveVideos();
        }, 100);
      }
    });


    // Handle page unload
    window.addEventListener('beforeunload', () => {
      this.cleanup();
    });
  }

  handleResponsiveChanges() {
    // Adjust video behavior based on screen size
    this.videos.forEach(videoData => {
      const { element: video, container } = videoData;
      
      if (this.isMobile) {
        // On mobile, be more conservative with autoplay
        if (video.autoplay && !video.muted) {
          video.pause();
        }
      }
    });
  }

  pauseAllVideos() {
    this.videos.forEach(videoData => {
      if (!videoData.element.paused) {
        this.pauseVideo(videoData.element);
      }
    });
  }

  resumeVisibleVideos() {
    this.videos.forEach(videoData => {
      const { element: video, container } = videoData;
      
      if (container.classList.contains('in-view') && video.hasAttribute('autoplay')) {
        this.playVideo(video);
      }
    });
  }

  resumeActiveVideos() {
    this.videos.forEach(videoData => {
      const { element: video, container } = videoData;
      const productBlock = container.closest('.product-block__image');
      
      // Check if this video's image block is currently active/visible
      if (productBlock && productBlock.classList.contains('product-block__image--active')) {
        // If video is in viewport and should be playing, resume it
        if (container.classList.contains('in-view') || this.isElementInViewport(video)) {
          if (video.paused && video.hasAttribute('autoplay')) {
            this.playVideo(video);
          }
        }
      }
    });
  }

  isElementInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  ensureVideoStaysActive(productBlock) {
    // Find the video container in this product block
    const videoContainer = productBlock.querySelector('.product-block__video-container');
    if (!videoContainer) return;

    const video = videoContainer.querySelector('video');
    const videoImageBlock = videoContainer.closest('.product-block__image');
    
    if (video && videoImageBlock) {
      // Force the video image block to be active
      const allImageBlocks = productBlock.querySelectorAll('.product-block__image');
      allImageBlocks.forEach(block => block.classList.remove('product-block__image--active'));
      videoImageBlock.classList.add('product-block__image--active');
      
      // Ensure video is playing
      if (video.paused && video.hasAttribute('autoplay')) {
        this.playVideo(video);
      }
      
      // Update image dots to reflect video is active (first dot)
      const dots = productBlock.querySelectorAll('.product-block__image-dot');
      if (dots.length > 0) {
        dots.forEach(dot => dot.classList.remove('product-block__image-dot--active'));
        dots[0].classList.add('product-block__image-dot--active');
      }
    }
  }


  handleVideoVisibility() {
    // Fallback for browsers without IntersectionObserver
    const checkVisibility = () => {
      this.videos.forEach(videoData => {
        const { element: video, container } = videoData;
        const rect = video.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        
        if (isVisible) {
          this.playVideo(video);
          container.classList.add('in-view');
        } else {
          this.pauseVideo(video);
          container.classList.remove('in-view');
        }
      });
    };

    // Check on scroll and resize
    window.addEventListener('scroll', throttle(checkVisibility, 100));
    window.addEventListener('resize', throttle(checkVisibility, 250));
    
    // Initial check
    checkVisibility();
  }

  cleanup() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    this.pauseAllVideos();
    this.videos = [];
  }

  // Public method to reinitialize videos (useful for AJAX loading)
  reinitialize() {
    this.cleanup();
    this.videos = [];
    this.initializeVideos();
  }
}

// Utility function for throttling
function throttle(func, limit) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productVideoManager = new ProductVideoManager();
  });
} else {
  window.productVideoManager = new ProductVideoManager();
}

// Reinitialize on AJAX page loads (for themes with AJAX navigation)
document.addEventListener('shopify:section:load', () => {
  if (window.productVideoManager) {
    window.productVideoManager.reinitialize();
  }
});

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductVideoManager;
}
